import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { TrendingUp, Calculate } from '@mui/icons-material';
import { cashService, CashPosition } from '../../services/cashService';
import { LoggerService } from '../../services/LoggerService';

interface CashProjectionChartProps {
  fundId: string;
  currency?: string;
}

interface ChartDataPoint {
  date: string;
  historical?: number;
  projected?: number;
  confidence_lower?: number;
  confidence_upper?: number;
  type: 'historical' | 'projected';
}

const CashProjectionChart: React.FC<CashProjectionChartProps> = ({ fundId, currency = 'EUR' }) => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [projectionMethod, setProjectionMethod] = useState<string>('moving_average');
  const [daysAhead, setDaysAhead] = useState<number>(30);

  useEffect(() => {
    loadChartData();
  }, [fundId, projectionMethod, daysAhead]);

  const loadChartData = async () => {
    if (!fundId) return;

    try {
      setLoading(true);

      // Get historical positions (last 30 days)
      const positionsResponse = await cashService.getFundCashPositions(fundId, {
        date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      });

      // Get projections
      const projectionsResponse = await cashService.getProjections(fundId, {
        method: projectionMethod,
      });

      // Combine historical and projected data
      const data: ChartDataPoint[] = [];

      // Add historical data
      positionsResponse.positions.forEach((pos: CashPosition) => {
        data.push({
          date: pos.date,
          historical: pos.closing_balance,
          type: 'historical',
        });
      });

      // Add projected data
      if (projectionsResponse.projections) {
        projectionsResponse.projections.forEach((proj: any) => {
          const existingIndex = data.findIndex((d) => d.date === proj.projection_date);
          if (existingIndex >= 0) {
            data[existingIndex].projected = proj.projected_balance;
            data[existingIndex].confidence_lower = proj.projected_balance * (1 - proj.confidence_level);
            data[existingIndex].confidence_upper = proj.projected_balance * (1 + proj.confidence_level);
          } else {
            data.push({
              date: proj.projection_date,
              projected: proj.projected_balance,
              confidence_lower: proj.projected_balance * (1 - proj.confidence_level),
              confidence_upper: proj.projected_balance * (1 + proj.confidence_level),
              type: 'projected',
            });
          }
        });
      }

      // Sort by date
      data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      setChartData(data);
    } catch (error) {
      LoggerService.error('Error loading chart data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateProjections = async () => {
    try {
      setLoading(true);
      await cashService.calculateProjections(fundId, daysAhead, projectionMethod);
      await loadChartData();
    } catch (error) {
      LoggerService.error('Error calculating projections:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading && chartData.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Cash Projections</Typography>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Method</InputLabel>
              <Select
                value={projectionMethod}
                onChange={(e) => setProjectionMethod(e.target.value)}
                label="Method"
              >
                <MenuItem value="moving_average">Moving Average</MenuItem>
                <MenuItem value="regression">Regression</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Calculate />}
              onClick={handleCalculateProjections}
              disabled={loading}
            >
              Calculate
            </Button>
          </Box>
        </Box>

        {chartData.length === 0 ? (
          <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
            No data available. Calculate projections to see the chart.
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tickFormatter={formatCurrency} />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                labelFormatter={(label) => formatDate(label)}
              />
              <Legend />
              <ReferenceLine
                x={chartData.find((d) => d.type === 'projected')?.date}
                stroke="#a855f7"
                strokeDasharray="3 3"
                label="Projection Start"
              />
              <Line
                type="monotone"
                dataKey="historical"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Historical Balance"
              />
              <Line
                type="monotone"
                dataKey="projected"
                stroke="#f59e0b"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ r: 4 }}
                name="Projected Balance"
              />
              <Line
                type="monotone"
                dataKey="confidence_lower"
                stroke="#f59e0b"
                strokeWidth={1}
                strokeDasharray="2 2"
                dot={false}
                name="Confidence Lower"
                strokeOpacity={0.3}
              />
              <Line
                type="monotone"
                dataKey="confidence_upper"
                stroke="#f59e0b"
                strokeWidth={1}
                strokeDasharray="2 2"
                dot={false}
                name="Confidence Upper"
                strokeOpacity={0.3}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default CashProjectionChart;

